import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  empty,
  errorResponse,
  isArray,
  isObject,
  prepareResponseData,
  reIndex,
} from "../../Utilities/utils";
import { Toast } from "primereact/toast";

// css
import "../students/Students.css";

// api
import sessionApi from "../../api/Session";
import termApi from "../../api/Terms";
import classApi from "../../api/Classes";
import paymentApi from "../../api/Payment";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import SelectField from "../../components/form/SelectField";
import FullPageLoader from "../../components/loader/FullPageLoader";
import { AuthContext } from "../Root/ProtectedRoute";
import Card from "../../components/card/Card";
import AssessmentTable from "../../components/tables/primeTable/assessment/AssessmentTable";
import { PaymentType } from "../../Utilities/types/PaymentTypes";
import ErrorAlert from "../../components/error/ErrorAlert";

const required = "This field is required!";
const validationSchema = Yup.object().shape({
  session_id: Yup.string().required(required),
  term_id: Yup.string().required(required),
});

const initialValues = {
  session_id: "",
  term_id: "",
};

const Assessment = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [paidForPortal, setPaidForPortal] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classData, setClassData] = useState({});
  const [reIndexedSubjects, setReIndexedSubjects] = useState({});
  const [studentAssessment, setStudentAssessment] = useState([]);
  const toastTR = useRef(null);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    getSessions();
    getTerms();
    getSubjectsInClass();
    getClassDetails();
  }, []);

  const getSessions = async () => {
    try {
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await sessionApi.getSessions(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Failed to fetch sessions!"
            : "Failed to fetch sessions"
        );
      }

      if (!empty(response_data) && response_data.success) {
        const sessionData = response_data.response;
        setSessions(sessionData);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getSubjectsInClass = async () => {
    try {
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await classApi.getSubjectsInClass(
        user?.currentClassId,
        schoolId,
        1000
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.success) ||
        !response_data.success
      ) {
        return setReIndexedSubjects({});
      } else {
        const classSubjects =
          !empty(response_data.response) && isArray(response_data.response)
            ? response_data.response
            : [];
        const reIndexedSubjects = reIndex(classSubjects, "_id");
        return setReIndexedSubjects(reIndexedSubjects);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getTerms = async () => {
    try {
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await termApi.getTerms(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Failed to fetch terms!"
            : "Failed to fetch terms"
        );
      }

      if (!empty(response_data) && !empty(response_data.response)) {
        const termData = response_data.response;
        setTerms(termData);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getClassDetails = async () => {
    try {
      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await classApi.getSingleClass(
        user?.currentClassId,
        schoolId
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setClassData({});
      } else {
        const data =
          !empty(response_data) &&
          !empty(response_data.response) &&
          isObject(response_data.response)
            ? response_data.response
            : {};
        return setClassData(data);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getAssessmentData = async (values) => {
    try {
      setIsLoading(true);
      const sessionId =
        !empty(values) && !empty(values.session_id) ? values.session_id : "";
      const termId =
        !empty(values) && !empty(values.term_id) ? values.term_id : "";

      const schoolId =
        !empty(user) && !empty(user.schoolId) ? user.schoolId : "";
      const response = await paymentApi.getPortalPaymentStatus(
        sessionId,
        termId,
        PaymentType.ACTIVATION.name,
        schoolId
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.success) {
        return setPaidForPortal(false);
      }
      setPaidForPortal(response_data?.response?.paymentStatus || false);

      const studentAssessmentData =
        !empty(user) && !empty(user.assessment) ? user.assessment : [];
      const currentStudentAssessmentData = studentAssessmentData.filter(
        (item) => {
          const itemSessionId =
            !empty(item) && !empty(item.sessionId) ? item.sessionId : "";
          const itemTermId =
            !empty(item) && !empty(item.termId) ? item.termId : "";
          if (itemSessionId === sessionId && itemTermId === termId) {
            const subjectId =
              !empty(item) && !empty(item.subjectId) ? item.subjectId : "";
            item.subjectCode =
              !empty(reIndexedSubjects) &&
              !empty(reIndexedSubjects[subjectId]) &&
              !empty(reIndexedSubjects[subjectId].code)
                ? reIndexedSubjects[subjectId].code
                : "";
            return (item.subjectTitle =
              !empty(reIndexedSubjects) &&
              !empty(reIndexedSubjects[subjectId]) &&
              !empty(reIndexedSubjects[subjectId].title)
                ? reIndexedSubjects[subjectId].title
                : "");
          }
        }
      );

      setStudentAssessment(
        isArray(currentStudentAssessmentData)
          ? currentStudentAssessmentData
          : []
      );
      setIsLoading(false);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const handleSessionChange = (setFieldValue) => (e) => {
    const { value } = e.target;
    setFieldValue("session_id", value);
  };
  const handleTermChange = (setFieldValue) => (e) => {
    const { value } = e.target;
    setFieldValue("term_id", value);
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            <MainHeader title="Assessment" />
            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={getAssessmentData}
              >
                {({ values, setFieldValue }) => (
                  <Form style={{ width: "100%" }}>
                    <div className="app_input_group">
                      <SelectField
                        labelTitle="Session"
                        placeholder="Select Session"
                        required={true}
                        name="session_id"
                        options={sessions}
                        height={50}
                        valueKey="id"
                        selectedOption={values.session_id}
                        handleChangeFunc={handleSessionChange(setFieldValue)}
                      />
                      <SelectField
                        labelTitle="Term"
                        required={true}
                        placeholder="Select Term"
                        name="term_id"
                        options={terms}
                        height={50}
                        valueKey="id"
                        selectedOption={values.term_id}
                        handleChangeFunc={handleTermChange(setFieldValue)}
                      />
                    </div>
                    <div className="form-button-container">
                      <ButtonIcon
                        height={45}
                        marginTop={5}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={300}
                        borderColor="#633ccd"
                        buttonText="Submit"
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
            <Card addStyle="w-100pc px-25">
              <MainHeader title="Assessment Data" marginTop={20} />
              {paidForPortal || empty(studentAssessment) ? (
                <AssessmentTable assessments={studentAssessment} />
              ) : (
                <ErrorAlert
                  title="Result Sheet Unavailable"
                  message="You have an outstanding portal levy for the selected session and term."
                />
              )}
            </Card>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </AppWrapper>
    </>
  );
};

export default Assessment;
